import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { ApartmentEditNavText } from '../../i18n/pages/apartmentEditNav';
import type { ApartmentDetailText } from '../../i18n/pages/apartmentDetail';
import type { Lang } from '../../i18n/types';
import { groupLabel } from './GroupTabs';
import { stripLeadingSymbols } from './groups';
import { SearchHit, SettingsEntry, searchSettings } from './settingsIndex';

interface Props {
  lang: Lang;
  detailText: ApartmentDetailText;
  text: ApartmentEditNavText;
  /** From `?find=`: prefilled, never auto-jumped. */
  initialQuery?: string;
  onPick: (entry: SettingsEntry) => void;
}

const SECTION_LABEL = (t: ApartmentDetailText, section: SettingsEntry['section']): string =>
  stripLeadingSymbols({
    general: t.tabGeneral, contact: t.tabContact, ubyport: t.tabUbyport, payments: t.tabPayments,
    citytax: t.tabCityTax, calendar: t.tabCalendar, guide: t.tabGuide, upsell: t.tabUpsell,
    deposit: t.tabDeposit, export: t.tabExport,
  }[section]);

/** Keyboard + mouse device: show the "/" hint. Touch: show example words instead. */
function hasKeyboardAndMouse(): boolean {
  try {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  } catch {
    return window.innerWidth > 640;
  }
}

const isTypingTarget = (el: EventTarget | null): boolean => {
  const n = el as HTMLElement | null;
  if (!n || !n.tagName) return false;
  return /^(INPUT|TEXTAREA|SELECT)$/.test(n.tagName) || n.isContentEditable;
};

/**
 * "Najít nastavení…": a combobox over the static settings index. Choosing a result hands the entry
 * to the page, which switches group and jumps to the field.
 */
const SettingsSearch: React.FC<Props> = ({ lang, detailText, text, initialQuery = '', onPick }) => {
  const [query, setQuery] = useState(initialQuery);
  const [open, setOpen] = useState(!!initialQuery);
  const [active, setActive] = useState(0);
  const [keyboardHint, setKeyboardHint] = useState(hasKeyboardAndMouse);
  const inputRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const hits: SearchHit[] = useMemo(() => searchSettings(query, lang), [query, lang]);
  const showList = open && query.trim().length > 0;

  useEffect(() => {
    let mq: MediaQueryList | null = null;
    const update = () => setKeyboardHint(hasKeyboardAndMouse());
    try {
      mq = window.matchMedia('(hover: hover) and (pointer: fine)');
      mq.addEventListener?.('change', update);
    } catch { /* no matchMedia: keep the initial guess */ }
    return () => { mq?.removeEventListener?.('change', update); };
  }, []);

  // "/" focuses the search unless the host is already typing somewhere.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;
      e.preventDefault();
      inputRef.current?.focus();
      inputRef.current?.select();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Click outside closes the list.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const pick = (hit: SearchHit | undefined) => {
    if (!hit) return;
    setQuery('');
    setOpen(false);
    setActive(0);
    onPick(hit.entry);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' && hits.length) {
      e.preventDefault();
      setOpen(true);
      setActive(a => (a + 1) % hits.length);
    } else if (e.key === 'ArrowUp' && hits.length) {
      e.preventDefault();
      setOpen(true);
      setActive(a => (a - 1 + hits.length) % hits.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      pick(hits[active] ?? hits[0]);
    } else if (e.key === 'Escape') {
      if (query || open) { setQuery(''); setOpen(false); } else inputRef.current?.blur();
    }
  };

  return (
    <div className="ae-search" ref={rootRef}>
      <label className="ae-sr" htmlFor="ae-search-input">{text.searchLabel}</label>
      <input
        id="ae-search-input" ref={inputRef} type="text" className="ae-search-input"
        role="combobox" aria-expanded={showList} aria-controls="ae-search-results" aria-autocomplete="list"
        aria-activedescendant={showList && hits.length ? `ae-hit-${active}` : undefined}
        autoComplete="off"
        placeholder={keyboardHint ? text.searchPlaceholderKeyboard : text.searchPlaceholderTouch}
        value={query}
        onChange={e => { setQuery(e.target.value); setOpen(true); setActive(0); }}
        onFocus={() => { if (query) setOpen(true); }}
        onKeyDown={onKeyDown}
      />
      {showList && (
        <ul id="ae-search-results" className="ae-search-results" role="listbox" aria-label={text.searchResults}
          onMouseDown={e => e.preventDefault()}>
          {hits.length === 0 && <li className="ae-search-none" role="presentation">{text.searchEmpty}</li>}
          {hits.map((hit, i) => (
            <li
              key={hit.entry.id} id={`ae-hit-${i}`} role="option" aria-selected={i === active}
              className={`ae-search-hit${i === active ? ' ae-active' : ''}`}
              onMouseEnter={() => setActive(i)} onClick={() => pick(hit)}
            >
              <b>{hit.label}</b>
              <small>{groupLabel(text, hit.entry.group)} › {SECTION_LABEL(detailText, hit.entry.section)}</small>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SettingsSearch;
